import React, { useState, useEffect, useCallback } from "react";
import { Col, Row, Modal, ModalBody, ModalHeader, Input } from "reactstrap";
import { Link } from "react-router-dom";
import { 
  getJobs, 
  getCompany2, 
  getCategoryById, 
  getApplications, 
  getJobOfferApplications, 
  getEmployees3, 
  saveJobOffer, 
  delete_saved_offers_by_job_employee, 
  get_saved_offers_by_employee_id 
} from "../../../services/userservice";
import { getPhotoUrl } from "../../../config";
import companyImg from "../../../assets/images/companyImg.jpg";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useEmployeeId, useUserRole } from '../../../hooks';
import { createMountedRef } from '../../../utils/validation';
import { batchStateUpdates } from '../../../utils/apiHelpers';
import Pagination from "../JobList2/Pagination";

const PAGE_SIZE_OPTIONS = [5, 10, 20];

const JobVacancy = ({ filters, categoryFilter }) => {
  const [jobs, setJobs] = useState([]);
  const [companies, setCompanies] = useState({});
  const [categories, setCategories] = useState({});
  const [applications, setApplications] = useState([]);
  const [favoriteJobIds, setFavoriteJobIds] = useState([]);
  const [modal, setModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [selectedJobApplications, setSelectedJobApplications] = useState([]);
  const [selectedJobOfferId, setSelectedJobOfferId] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const employeeId = useEmployeeId();
  const userRol = useUserRole();

  // Reset to first page when filters or page size change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters.department, filters.city, filters.categoryId, filters.companyName, pageSize]);

  /**
   * Carga información de empresas y categorías para los trabajos
   */
  const loadJobMetadata = useCallback((jobsData, isMountedCheck) => {
    jobsData.forEach(job => {
      // Cargar información de la empresa
      getCompany2(job.company_id)
        .then(companyData => {
          if (!isMountedCheck()) return;
          setCompanies(prev => ({
            ...prev,
            [job.company_id]: companyData
          }));
        })
        .catch(error => console.error(error));

      // Cargar información de la categoría
      getCategoryById(job.category_id)
        .then(categoryData => {
          if (!isMountedCheck()) return;
          setCategories(prev => ({
            ...prev,
            [job.category_id]: categoryData.category_name
          }));
        })
        .catch(error => console.error(error));
    });
  }, []);

  /**
   * Carga los datos iniciales (trabajos, aplicaciones, favoritos)
   */
  useEffect(() => {
    const { isMountedRef, cleanup } = createMountedRef();
    const isMountedCheck = () => isMountedRef.current;

    const loadData = async () => {
      try {
        const categoryId = filters.categoryId ?? categoryFilter;
        const fetchJobsPromise = getJobs({
          department: filters.department || undefined,
          city: filters.city || undefined,
          categoryId: categoryId != null ? categoryId : undefined
        });

        const [jobsData, applicationsData] = await Promise.all([
          fetchJobsPromise,
          getApplications()
        ]);

        if (!isMountedCheck()) return;

        // Actualizar estados en batch
        batchStateUpdates([
          { setter: setJobs, value: jobsData },
          { setter: setApplications, value: applicationsData }
        ], isMountedCheck);

        // Cargar metadata de trabajos
        loadJobMetadata(jobsData, isMountedCheck);

        // Cargar favoritos si hay employeeId
        if (employeeId) {
          const savedOffersData = await get_saved_offers_by_employee_id(employeeId);
          if (isMountedCheck()) {
            const savedJobIds = savedOffersData.map(offer => offer.job_offer_id);
            setFavoriteJobIds(savedJobIds);
          }
        }
      } catch (error) {
        console.error('Error loading job data:', error);
      }
    };

    loadData();
    return cleanup;
  }, [categoryFilter, employeeId, loadJobMetadata, filters.department, filters.city, filters.categoryId]);

  const filterJobs = (jobs) => {
    return jobs.filter(job => {
      if (job.suspended || !job.status) return false;
      const company = companies[job.company_id] || {};
      const matchesCompanyName = !filters.companyName ||
        (company.name?.toLowerCase() || '').includes(filters.companyName.toLowerCase());
      return matchesCompanyName;
    });
  };

  const filteredJobs = filterJobs(jobs);
  const totalFiltered = filteredJobs.length;
  const start = totalFiltered === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, totalFiltered);
  const paginatedJobs = filteredJobs.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const getApplicationCount = (jobOfferId) => {
    return applications.filter(app => app.job_offer_id === jobOfferId).length;
  };

  const openModal = async (jobTitle, employee_list) => {
    setModalTitle(jobTitle);

    const employeeDetailsList = await Promise.all(
      employee_list.map(async (employee) => {
        const employeeDetails = await getEmployees3(employee.employee_id);
        return {
          user_name: employeeDetails.name,
          user_last_name: employeeDetails.lastname,
          user_id: employeeDetails.id
        };
      })
    );

    setSelectedJobApplications(employeeDetailsList);
    setModal(true);
  };

  const toggleModal = () => {
    setModal(!modal);
  };

  const handleClickPostulaciones = (jobVacancyDetails) => {
    setSelectedJobOfferId(jobVacancyDetails.id);
    getJobOfferApplications(jobVacancyDetails.id)
      .then(data => openModal(jobVacancyDetails.title, data))
      .catch(error => console.log(error));
  };

  /**
   * Maneja la acción de agregar/quitar favoritos
   */
  const handleFavoriteToggle = useCallback(async (jobOfferId) => {
    const user = localStorage.getItem("id");
    
    if (!user || user === 'null') {
      toast.error("Por favor inicie sesión para continuar");
      return;
    }

    if (!employeeId) {
      toast.error("ID de empleado no encontrado");
      return;
    }

    const isFavorite = favoriteJobIds.includes(jobOfferId);

    try {
      if (isFavorite) {
        await delete_saved_offers_by_job_employee(jobOfferId, employeeId);
        setFavoriteJobIds(prev => prev.filter(id => id !== jobOfferId));
        toast.success("Oferta eliminada de favoritos");
      } else {
        const saveJobData = { job_offer_id: jobOfferId, employee_id: employeeId };
        await saveJobOffer(saveJobData);
        setFavoriteJobIds(prev => [...prev, jobOfferId]);
        toast.success("Oferta agregada a favoritos");
      }
    } catch (error) {
      const action = isFavorite ? "eliminar de" : "agregar a";
      toast.error(`Error al ${action} favoritos`);
      console.error(error);
    }
  }, [employeeId, favoriteJobIds]);

  return (
    <React.Fragment>
      <ToastContainer />
      <style>
        {`
          .image-container {
            width: 157px;
            height: 87px;
            overflow: hidden;
            position: relative;
          }

          .image-container img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            position: absolute;
            top: 0;
            left: 0;
          }

          .favorite-icon i.red {
            color: red;
          }

          .clickable-text {
            color: blue;
            text-decoration: underline;
            cursor: pointer;
          }
        `}
      </style>
      <div className="list-results-bar job-grid-results-bar">
        <span className="job-grid-results-text">
          Mostrando {totalFiltered === 0 ? 0 : start}-{end} de {totalFiltered} resultados
        </span>
        {(userRol === "2" || userRol === 2) && (
          <div className="job-grid-page-size-wrap">
            <label htmlFor="pageSizeSelect">Ofertas por página:</label>
            <Input
              id="pageSizeSelect"
              type="select"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="job-grid-page-size-select"
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </Input>
          </div>
        )}
      </div>
      {paginatedJobs.map((jobVacancyDetails, key) => (
        <div
          key={key}
          className={
            jobVacancyDetails.addclassNameBookmark === true
              ? "job-box bookmark-post card mt-4"
              : "job-box card mt-4"
          }
        >
          <div className="p-4">
            <Row>
              <Col lg={2}>
                <Link to="/companydetails">
                  <div className="image-container">
                    <img
                      className="img-fluid rounded-3"
                      src={getPhotoUrl(companies[jobVacancyDetails.company_id]?.photo) || companyImg}
                      alt=""
                      onError={(e) => { e.target.src = companyImg; }}
                    />
                  </div>
                </Link>
              </Col>
              <Col lg={10}>
                <div className="mt-3 mt-lg-0">
                  <h5 className="fs-17 mb-1">
                    <Link onClick={() => localStorage.setItem("jobs", jobVacancyDetails.id)} to={`/jobdetails/${jobVacancyDetails.id}`} className="text-dark">
                      {jobVacancyDetails.title}
                    </Link>{" "}
                    <small className="text-muted fw-normal">
                      ({categories[jobVacancyDetails.category_id]})
                    </small>
                  </h5>
                  <ul className="list-inline mb-0">
                    <li className="list-inline-item">
                      <p className="text-muted fs-14 mb-0">
                        {jobVacancyDetails.description}
                      </p>
                    </li>
                    <li className="list-inline-item">
                      <p className="text-muted fs-14 mb-0">
                        <i className="mdi mdi-map-marker"></i>{" "}
                        {jobVacancyDetails.city}
                      </p>
                    </li>
                    <li className="list-inline-item">
                      <p className="text-muted fs-14 mb-0">
                        <i className="uil uil-wallet"></i>{" "}
                        {jobVacancyDetails.salary}
                      </p>
                    </li>
                  </ul>
                  <div className="mt-2">
                    <span
                      className={
                        jobVacancyDetails.fullTime === true
                          ? "badge bg-success-subtle text-success fs-13 mt-1 mx-1"
                          : jobVacancyDetails.partTime === true
                          ? "badge bg-danger-subtle text-danger fs-13 mt-1 mx-1"
                          : jobVacancyDetails.freeLance === true
                          ? "badge bg-primary-subtle text-primary fs-13 mt-1 mx-1"
                          : jobVacancyDetails.internship === true
                          ? "badge bg-blue-subtle text-blue fs-13 mt-1"
                          : ""
                      }
                    >
                      {jobVacancyDetails.timing}
                    </span>
                  </div>
                  <div className="mt-2">
                    <span className="text-muted fs-13">Publicado el: {new Date(jobVacancyDetails.created_at).toLocaleDateString()}</span>
                  </div>
                  {/* El rol 1 pertenece a el usuario empresa */}
                  {userRol === '1' ? 
                    <div className="mt-2">
                      <span className="text-muted fs-13 clickable-text" onClick={ () => {handleClickPostulaciones(jobVacancyDetails)}}>
                        Postulaciones: {getApplicationCount(jobVacancyDetails.id)}
                      </span>
                    </div>
                    : 
                    <div></div>
                  }
                </div>
              </Col>
            </Row>
            <div className="favorite-icon">
              <Link
                to="#jobGrid"
                onClick={() => handleFavoriteToggle(jobVacancyDetails.id)}
              >
                <i className={`mdi ${favoriteJobIds.includes(jobVacancyDetails.id) ? 'mdi-cards-heart red' : 'mdi-heart-outline'}`}></i>
              </Link>
            </div>
          </div>
          <div className="p-3 bg-light">
            <Row className="justify-content-between">
              <Col md={8}>
                <div>
                  <ul className="list-inline mb-0">
                    <li className="list-inline-item">
                      <i className="uil uil-tag"></i> Keywords :
                    </li>
                    <li className="list-inline-item">
                      <Link to="#" className="primary-link text-muted">
                        Diseño
                      </Link>
                      ,
                    </li>
                    <li className="list-inline-item">
                      <Link to="#" className="primary-link text-muted">
                        Desarrollador
                      </Link>
                    </li>
                  </ul>
                </div>
              </Col>

              <Col md={4}>
                <div className="text-md-end">
                  <Link onClick={() => localStorage.setItem("jobs", jobVacancyDetails.id)} to={`/jobdetails/${jobVacancyDetails.id}`} className="primary-link">
                    Aplicar Ahora <i className="mdi mdi-chevron-double-right"></i>
                  </Link>
                </div>
              </Col>
            </Row>
          </div>
        </div>
      ))}
      <Pagination
        totalItems={totalFiltered}
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
      />
      <Modal isOpen={modal} toggle={toggleModal} centered>
        <ModalHeader toggle={toggleModal}>Postulaciones para {modalTitle}</ModalHeader>
        <ModalBody>
          {selectedJobApplications.length > 0 ? (
            <ul>
              {selectedJobApplications.map((application, index) => (
                <Link
                  to="/candidatedetails"
                  state={selectedJobOfferId ? { returnJobOfferId: selectedJobOfferId, returnPage: 1, returnPageSize: 10 } : undefined}
                  onClick={() => localStorage.setItem("employee", application.user_id)}
                  key={index}
                >
                  <li>{application.user_name} {application.user_last_name}</li>
                </Link>
              ))}
            </ul>
          ) : (
            <p>No hay postulaciones para este empleo.</p>
          )}
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default JobVacancy;
